import React from 'react';
import PropTypes from 'prop-types';
import {Button, ButtonGroup, Col, Dropdown, Form, Row} from 'react-bootstrap';
import Check from '@mui/icons-material/CheckRounded';
import Close from '@mui/icons-material/CloseRounded';
import Collection from '@mui/icons-material/GridViewRounded';
import Funnel from '@mui/icons-material/FilterListRounded';
import List from '@mui/icons-material/ViewListRounded';
import Map from '@mui/icons-material/MapRounded';
import SortDown from '@mui/icons-material/SortRounded';
import {lifecycleStates} from '../../../shared/constants/lifecycleStates';
import {PAGE_SIZE, sortOptions} from '../constants/edgesConstants';

const FilterDropdownItem = ({label, active, onClick}) => (
  <Dropdown.Item onClick={onClick} className="d-flex justify-content-between align-items-center">
    {label}
    {active && <Check fontSize="small"/>}
  </Dropdown.Item>
);

FilterDropdownItem.propTypes = {
  label: PropTypes.string.isRequired,
  active: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

const SortDropdownItem = ({value, label, currentSort, onSelect}) => (
  <Dropdown.Item onClick={() => onSelect(value)} className="d-flex justify-content-between align-items-center">
    {label}
    {currentSort === value && <Check fontSize="small"/>}
  </Dropdown.Item>
);

SortDropdownItem.propTypes = {
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  currentSort: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
};

const EdgesToolbar = ({
  searchValue,
  setSearchValue,
  viewMode,
  setViewMode,
  filterStatus,
  setFilterStatus,
  filterLifecycle,
  setFilterLifecycle,
  filterTenant,
  setFilterTenant,
  sortOption,
  setSortOption,
  availableTenants,
  toggleFilter,
  isSelected,
  resultCount,
  currentPage,
  totalPages,
}) => (
  <>
    <Row>
      <Col className="d-flex">
        <Form.Control
          type="text"
          placeholder="Search..."
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
        />
        <ButtonGroup className="float-start ms-2">
          <Button active={viewMode === 'card'} onClick={() => setViewMode('card')}><Collection/></Button>
          <Button active={viewMode === 'table'} onClick={() => setViewMode('table')}><List/></Button>
          <Button active={viewMode === 'map'} onClick={() => setViewMode('map')}><Map/></Button>
        </ButtonGroup>
      </Col>
      <Col className="d-flex gap-2 justify-content-end">
        <Dropdown autoClose="outside">
          <Dropdown.Toggle variant="outline-secondary" id="dropdown-status">
            <Funnel className="me-2"/>
            {filterStatus.length > 0 ? `Status (${filterStatus.length})` : 'Status'}
            {filterStatus.length > 0 && (
              <Close
                onClick={(e) => {e.stopPropagation(); setFilterStatus([]);}}
                style={{fontSize: '16px', marginLeft: '6px', opacity: 0.6, verticalAlign: 'middle'}}
              />
            )}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <FilterDropdownItem
              label="Healthy"
              active={isSelected(filterStatus, 'active')}
              onClick={() => toggleFilter(filterStatus, setFilterStatus, 'active')}
            />
            <FilterDropdownItem
              label="Offline"
              active={isSelected(filterStatus, 'issue')}
              onClick={() => toggleFilter(filterStatus, setFilterStatus, 'issue')}
            />
            <FilterDropdownItem
              label="Unhealthy"
              active={isSelected(filterStatus, 'unhealthy')}
              onClick={() => toggleFilter(filterStatus, setFilterStatus, 'unhealthy')}
            />
            <FilterDropdownItem
              label="Unknown"
              active={isSelected(filterStatus, 'unknown')}
              onClick={() => toggleFilter(filterStatus, setFilterStatus, 'unknown')}
            />
          </Dropdown.Menu>
        </Dropdown>

        <Dropdown autoClose="outside">
          <Dropdown.Toggle variant="outline-secondary" id="dropdown-lifecycle">
            <Funnel className="me-2"/>
            {filterLifecycle.length > 0 ? `Lifecycle (${filterLifecycle.length})` : 'Lifecycle'}
            {filterLifecycle.length > 0 && (
              <Close
                onClick={(e) => {e.stopPropagation(); setFilterLifecycle([]);}}
                style={{fontSize: '16px', marginLeft: '6px', opacity: 0.6, verticalAlign: 'middle'}}
              />
            )}
          </Dropdown.Toggle>
          <Dropdown.Menu style={{maxHeight: '300px', overflowY: 'auto'}}>
            {Object.keys(lifecycleStates).map((s) => (
              <FilterDropdownItem
                key={s}
                label={s}
                active={isSelected(filterLifecycle, s)}
                onClick={() => toggleFilter(filterLifecycle, setFilterLifecycle, s)}
              />
            ))}
          </Dropdown.Menu>
        </Dropdown>

        {availableTenants.length > 1 && (
          <Dropdown autoClose="outside">
            <Dropdown.Toggle variant="outline-secondary" id="dropdown-tenant">
              <Funnel className="me-2"/>
              {filterTenant.length > 0 ? `Tenant (${filterTenant.length})` : 'Tenant'}
              {filterTenant.length > 0 && (
                <Close
                  onClick={(e) => {e.stopPropagation(); setFilterTenant([]);}}
                  style={{fontSize: '16px', marginLeft: '6px', opacity: 0.6, verticalAlign: 'middle'}}
                />
              )}
            </Dropdown.Toggle>
            <Dropdown.Menu style={{maxHeight: '300px', overflowY: 'auto'}}>
              {availableTenants.map((name) => (
                <FilterDropdownItem
                  key={name}
                  label={name}
                  active={isSelected(filterTenant, name)}
                  onClick={() => toggleFilter(filterTenant, setFilterTenant, name)}
                />
              ))}
            </Dropdown.Menu>
          </Dropdown>
        )}

        <Dropdown>
          <Dropdown.Toggle variant="outline-secondary" id="dropdown-sort">
            <SortDown className="me-2"/>
            Sort ({sortOptions[sortOption]})
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {Object.entries(sortOptions).map(([value, label]) => (
              <SortDropdownItem
                key={value}
                value={value}
                label={label}
                currentSort={sortOption}
                onSelect={setSortOption}
              />
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </Col>
    </Row>
    <div className="text-muted ms-1 mb-1 mt-2">
      {resultCount} Edge{resultCount !== 1 && 's'} found
      {totalPages > 1 &&
        ` — showing ${(currentPage - 1) * PAGE_SIZE + 1}–${Math.min(currentPage * PAGE_SIZE, resultCount)}`}
    </div>
  </>
);

EdgesToolbar.propTypes = {
  searchValue: PropTypes.string.isRequired,
  setSearchValue: PropTypes.func.isRequired,
  viewMode: PropTypes.string.isRequired,
  setViewMode: PropTypes.func.isRequired,
  filterStatus: PropTypes.array.isRequired,
  setFilterStatus: PropTypes.func.isRequired,
  filterLifecycle: PropTypes.array.isRequired,
  setFilterLifecycle: PropTypes.func.isRequired,
  filterTenant: PropTypes.array.isRequired,
  setFilterTenant: PropTypes.func.isRequired,
  sortOption: PropTypes.string.isRequired,
  setSortOption: PropTypes.func.isRequired,
  availableTenants: PropTypes.array.isRequired,
  toggleFilter: PropTypes.func.isRequired,
  isSelected: PropTypes.func.isRequired,
  resultCount: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
};

export default EdgesToolbar;
